import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import { STATE_FILTER_OPTIONS } from 'constants/states';
import { AdminListPageConfig, AdminListPageRenderer, useAdminListPage } from 'hooks/useAdminListPage';
import { Location } from 'models/flexible/Location';
import { PERMISSIONS } from 'models/Profile';
import type { AdminLocationIndexFields } from 'services/api/fields/admin/location';

const LOCATION_INCLUDES = ['groupsCount'] as const;

type LocationRow = Location<AdminLocationIndexFields<(typeof LOCATION_INCLUDES)[number]>>;
import { adminIndexClients } from 'services/api/admin/clients';
import { adminIndexLocations, adminLocationsQueryKeys, archiveLocation } from 'services/api/admin/locations';
import { colors } from 'styles/theme/colors';
import AwardIcon from 'svg/AwardIcon';
import DeactivateIcon from 'svg/DeactivateIcon';
import GearIcon from 'svg/GearIcon';
import PencilIcon from 'svg/PencilIcon';

import AssociatedGroupsSlideout from './AssociatedGroupsSlideout';
import PreferredProvidersSlideout from './PreferredProvidersSlideout';

const config: AdminListPageConfig<LocationRow> = {
  title: 'Locations',
  columnStorageKey: 'admin.locations',
  queryKey: adminLocationsQueryKeys.index,
  fetchFn: (params, signal) => adminIndexLocations(params, signal, [...LOCATION_INCLUDES]),
  includes: ['groups_count'],
  fetchParams: {
    defaultSort: 'name asc',
    pageSize: 10,
  },
  searchPlaceholder: 'Search Locations',
  showResultCount: true,

  addButton: {
    label: 'Add Location',
    path: '/admin/locations/new',
    permission: 'adminGroupCreate',
  },

  filters: [
    {
      key: 'clientId',
      label: 'Client',
      type: 'dropdown',
      fetchConfig: {
        queryKey: ['adminClients', 'locations', 'filter'],
        fetchFn: adminIndexClients,
        params: { sortBy: 'name asc' },
      },
      getOptionLabel: (o) => o.name,
      getOptionValue: (o) => o.id,
      toRequest: (value) => value?.id || undefined,
    },
    {
      key: 'state',
      label: 'State',
      type: 'dropdown',
      staticOptions: STATE_FILTER_OPTIONS,
      getOptionLabel: (o) => o.name,
      getOptionValue: (o) => o.id,
      toRequest: (value) => value?.id || undefined,
    },
  ],

  columns: [
    {
      id: 'name',
      header: 'Location',
      render: (row) => <BoldText>{row.name || '\u2014'}</BoldText>,
    },
    {
      id: 'clientName',
      header: 'Client Name',
      sortable: false,
      render: (row) => row.client?.name || '\u2014',
    },
    {
      id: 'address',
      header: 'Address',
      sortable: false,
      render: (row) => row.formattedAddress || '\u2014',
    },
    {
      id: 'groupsCount',
      header: 'Associated Groups',
      sortable: false,
      render: (row, ctx) => {
        if (!row.groupsCount) return '\u2014';
        return (
          <GroupsCountLink onClick={() => ctx.openSlideout('associatedGroups', row)}>{row.groupsCount}</GroupsCountLink>
        );
      },
    },
  ],

  rowActions: [
    {
      label: 'Edit',
      icon: <PencilIcon width={18} height={18} />,
      permission: 'adminGroupView',
      onClick: (row, { navigate }) => navigate(`/admin/locations/${row.id}/edit`),
    },
    {
      label: 'QHIN Configuration',
      icon: <GearIcon width={18} height={18} />,
      permission: 'adminQhinConfigurationView',
      onClick: (row, { navigate }) => navigate(`/admin/locations/${row.id}/qhin-configuration`),
    },
    {
      label: 'Manage preferred providers',
      icon: <AwardIcon width={18} height={18} />,
      permission: 'adminGroupView',
      onClick: (row, ctx) => ctx.openSlideout('preferredProviders', row),
    },
    {
      label: 'Archive',
      icon: <DeactivateIcon width={18} height={18} color={colors.red100} />,
      danger: true,
      permission: 'adminGroupEdit',
      visible: (row) => !row.groupsCount,
      onClick: (row, { openModal }) => openModal('archive', row),
    },
  ],

  modals: {
    archive: {
      actionText: 'Archive',
      objType: 'location',
      onConfirm: async (row, ctx) => {
        try {
          await archiveLocation(row.id);
        } catch (e: any) {
          const errors = e?.response?.data?.errors;
          const message = errors?.base?.join(', ') || 'Unable to archive this location.';
          ctx.toast(message);
        }
      },
    },
  },

  slideouts: {
    associatedGroups: {
      render: (row, onClose) => (
        <AssociatedGroupsSlideout locationId={row.id} locationName={row.name} handleClose={onClose} />
      ),
    },
    preferredProviders: {
      render: (row, onClose) => (
        <PreferredProvidersSlideout
          // A fresh panel per location, like the customer page: its draft is per location.
          key={row.id}
          locationId={row.id}
          locationName={row.name}
          clientId={row.client.id}
          handleClose={onClose}
        />
      ),
    },
  },
};

export default function Locations() {
  const state = useAdminListPage(config);
  const navigate = useNavigate();

  const configWithImport = useMemo(
    () => ({
      ...config,
      headerExtra: state.profile.has(PERMISSIONS.adminBulkImportCreate) ? (
        <Button variant='outline-primary' onClick={() => navigate('/admin/bulk-import/new?type=locations')}>
          Bulk import
        </Button>
      ) : undefined,
    }),
    [state.profile, navigate]
  );

  return <AdminListPageRenderer config={configWithImport} state={state} />;
}

const BoldText = styled.span`
  font-weight: 500;
`;

const GroupsCountLink = styled.span`
  color: ${({ theme }) => theme.colors.blue100};
  cursor: pointer;
  text-decoration: underline;
`;
